import React from 'react';
import { Link as ReactLink, useLocation, useMatches } from 'react-router-dom';
import { Breadcrumbs, Stack, Link } from '@mui/material';
import Text from './Text';

interface CrumbItem {
  name: React.ReactNode;
  to: string;
  navState?: Record<string, any> | null;
  isLink: boolean;
}

function BreadCrumb() {
  const location = useLocation();
  const matches = useMatches();
  const state = (location.state ?? {}) as Record<string, any>;
  const pathname = location.pathname;

  const isDevGroupHierarchy = pathname.startsWith('/developer-group/');
  const hasSpvLevel = pathname.includes('/view-spv/');
  const hasProjectLevel = pathname.includes('/view-project/');

  let crumbs: CrumbItem[] = [];

  if (isDevGroupHierarchy) {
    // groupId is in state when navigating from list → group, or from group → spv/project
    const groupId = state.groupId;
    const spvId = state.spvId;

    // Root: always show Developer Group list link
    crumbs.push({
      name: 'Developer Group',
      to: '/developer-group',
      navState: null,
      isLink: true,
    });

    // Level 2: specific Developer Group
    if (state.groupName) {
      const isDeeperLevel = hasSpvLevel || hasProjectLevel;
      crumbs.push({
        name: state.groupName,
        to: `/developer-group/${groupId}`,
        navState: {
          groupName: state.groupName,
          groupId,
          promoterName: state.promoterName ?? '',
        },
        // Only a link when the user is at a deeper level (SPV or Project)
        isLink: isDeeperLevel,
      });
    }

    // Level 3: SPV
    if (hasSpvLevel && state.spvName) {
      crumbs.push({
        name: state.spvName,
        to: `/developer-group/${groupId}/view-spv/${spvId}`,
        navState: {
          spvId,
          spvName: state.spvName,
          groupName: state.groupName,
          groupId,
        },
        // Only a link when the user is at the Project level
        isLink: hasProjectLevel,
      });
    }

    // Level 4: Project
    if (hasProjectLevel && state.projectName) {
      crumbs.push({
        name: state.projectName,
        to: pathname,
        navState: null,
        isLink: false,
      });
    }
  } else {
    // Non-hierarchy pages (Sales Executive, Investor Details, etc.)
    // Fall back to route handle.crumb logic.
    interface RouteMatch {
      handle?: { crumb?: (data: any) => React.ReactNode };
      pathname?: string;
    }
    const handleCrumbs = (matches as RouteMatch[])
      .filter(m => Boolean(m.handle?.crumb))
      .map(m => ({
        name: m.handle?.crumb?.(state) ?? '',
        to: m.pathname ?? '',
        navState: null,
        isLink: true,
      }));

    if (handleCrumbs.length === 0) {
      return (
        <div className="RemoveBreadCrumbStyle">
          <Stack spacing={2}>
            <Breadcrumbs separator="›" aria-label="breadcrumb" />
          </Stack>
        </div>
      );
    }

    crumbs = [
      { name: 'Dashboard', to: '/', navState: null, isLink: true },
      ...handleCrumbs,
    ];
  }

  return (
    <div className="BreadCrumbStyle">
      <Stack spacing={2}>
        <Breadcrumbs separator="›" aria-label="breadcrumb">
          {crumbs.map((crumb, index) => {
            const isLast = index === crumbs.length - 1;
            if (!crumb.isLink || isLast) {
              return (
                <Text
                  key={index}
                  style={{
                    fontSize: 14,
                    color: isLast ? '#104960' : '#757575',
                    fontWeight: isLast ? 600 : 400,
                  }}>
                  {crumb.name}
                </Text>
              );
            }
            return (
              <Link key={index} underline="hover" color="inherit">
                <ReactLink
                  to={crumb.to}
                  state={crumb.navState}
                  style={{ textDecoration: 'none' }}>
                  <Text style={{ fontSize: 14, color: '#757575' }}>
                    {crumb.name}
                  </Text>
                </ReactLink>
              </Link>
            );
          })}
        </Breadcrumbs>
      </Stack>
    </div>
  );
}

export default BreadCrumb;
